// Infinite scroll for the main collection grid.
//
// On initial render: the section paginates by 24 and shows the standard
// numbered pagination. With JS, the pagination is visually hidden and
// instead an IntersectionObserver sentinel near the bottom triggers a
// fetch of the next page URL. Fetched response is parsed, its grid <li>
// items are appended, and the next-page URL is updated.
//
// Filters: facets.js replaces #ProductGridContainer.innerHTML when a
// filter changes. We watch for that via MutationObserver and re-init
// from the new pagination block.

(function () {
  const CONTAINER_ID = 'ProductGridContainer';
  const GRID_ID = 'product-grid';
  const SENTINEL_ID = 'infinite-scroll-sentinel';
  const PAGE_HIDDEN_CLASS = 'pagination--js-hidden';
  const DEBUG = true; // set to false once verified

  let nextUrl = null;
  let loading = false;
  let observer = null;
  let sentinelEl = null;
  let prevPath = null;

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[infinite-scroll]'].concat(Array.from(arguments))); } catch (_) {}
  }
  // Expose state for quick inspection from devtools.
  window.__infiniteScroll = {
    get nextUrl() { return nextUrl; },
    get loading() { return loading; },
    get sentinel() { return sentinelEl; },
    get observer() { return observer; },
    forceFetch() { return fetchNextPage(); },
  };

  function findNextUrl(root) {
    // Pagination next link is rendered with aria-label including "next".
    const anchors = (root || document).querySelectorAll('.pagination__list a[aria-label]');
    for (const a of anchors) {
      if (/next/i.test(a.getAttribute('aria-label') || '')) return a.href;
    }
    return null;
  }

  function getCurrentSearchParams() {
    return window.location.search; // includes leading "?" or empty
  }

  function buildPageUrl(absoluteUrl) {
    // Combine the fetched URL's page param with the current filter URL params.
    try {
      const u = new URL(absoluteUrl, window.location.origin);
      const page = u.searchParams.get('page') || '2';
      const current = new URLSearchParams(window.location.search);
      current.set('page', page);
      return window.location.pathname + '?' + current.toString();
    } catch (_) {
      return absoluteUrl;
    }
  }

  async function fetchNextPage() {
    if (!nextUrl || loading) return;
    loading = true;

    const url = buildPageUrl(nextUrl);
    try {
      const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error('Failed: ' + res.status);
      const text = await res.text();
      const dom = new DOMParser().parseFromString(text, 'text/html');

      const newGrid = dom.getElementById(GRID_ID);
      const currentGrid = document.getElementById(GRID_ID);
      if (!newGrid || !currentGrid) {
        teardown();
        return;
      }

      const fragment = document.createDocumentFragment();
      Array.from(newGrid.children).forEach(li => fragment.appendChild(li));
      currentGrid.appendChild(fragment);

      // Find next URL from the freshly fetched DOM's pagination.
      nextUrl = findNextUrl(dom);
      if (!nextUrl) {
        teardown();
      } else if (observer && sentinelEl) {
        // Re-observe so the callback fires again on the next render — the
        // IntersectionObserver doesn't auto-refire if the sentinel was
        // already intersecting and stayed intersecting through the append.
        observer.unobserve(sentinelEl);
        // Defer the re-observe one tick so the DOM growth takes effect first.
        requestAnimationFrame(() => {
          if (sentinelEl && observer) observer.observe(sentinelEl);
          // Belt-and-suspenders: if the sentinel is still well inside the
          // rootMargin after grid growth, fire another fetch right away.
          if (sentinelEl && nextUrl && !loading) {
            const rect = sentinelEl.getBoundingClientRect();
            const triggerBelow = window.innerHeight + 1200;
            if (rect.top <= triggerBelow) fetchNextPage();
          }
        });
      }
    } catch (e) {
      console.error('[infinite-scroll]', e);
      teardown();
    } finally {
      loading = false;
    }
  }

  function teardown() {
    if (observer && sentinelEl) observer.unobserve(sentinelEl);
    if (sentinelEl) sentinelEl.remove();
    sentinelEl = null;
    nextUrl = null;
    // If pagination is hidden, show it so user can still navigate.
    document.querySelectorAll('.' + PAGE_HIDDEN_CLASS).forEach(el => el.classList.remove(PAGE_HIDDEN_CLASS));
  }

  function init() {
    log('init() running');
    // Reset previous state if re-initing after a filter change.
    if (sentinelEl) {
      if (observer) observer.unobserve(sentinelEl);
      sentinelEl.remove();
      sentinelEl = null;
    }

    const grid = document.getElementById(GRID_ID);
    if (!grid) { log('no #product-grid found, aborting'); return; }

    nextUrl = findNextUrl(document);
    log('nextUrl on init =', nextUrl);
    if (!nextUrl) {
      // Nothing more to load — leave pagination shown if it was rendered
      // (it won't be, since paginate.pages <= 1, but harmless).
      return;
    }

    // Hide the rendered pagination since JS is doing the work.
    document.querySelectorAll('.pagination-wrapper').forEach(el => {
      el.classList.add(PAGE_HIDDEN_CLASS);
    });

    // Insert sentinel right after the grid.
    sentinelEl = document.createElement('div');
    sentinelEl.id = SENTINEL_ID;
    sentinelEl.setAttribute('aria-hidden', 'true');
    sentinelEl.style.cssText = 'height:1px;width:100%;';
    grid.parentNode.insertBefore(sentinelEl, grid.nextSibling);
    log('sentinel inserted', sentinelEl);

    if (!observer) {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          log('observer callback isIntersecting=', entry.isIntersecting);
          if (entry.isIntersecting) fetchNextPage();
        }
      }, {
        // Start loading well before the user reaches the bottom.
        rootMargin: '0px 0px 1200px 0px',
        threshold: 0,
      });
    }
    observer.observe(sentinelEl);
    log('observing sentinel');

    // Scroll-based fallback in case IntersectionObserver doesn't fire
    // (e.g. weird stacking contexts, browser bugs). Throttled to ~10/s.
    if (!init._scrollAttached) {
      init._scrollAttached = true;
      let scrollTicking = false;
      const onScroll = () => {
        if (scrollTicking || !sentinelEl || !nextUrl || loading) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
          scrollTicking = false;
          if (!sentinelEl) return;
          const rect = sentinelEl.getBoundingClientRect();
          if (rect.top <= window.innerHeight + 1200) {
            log('scroll fallback fetching, rect.top=', rect.top);
            fetchNextPage();
          }
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  function watchForGridReplace() {
    // facets.js replaces #ProductGridContainer.innerHTML on filter change.
    // Watch for that to re-init from the new pagination.
    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    new MutationObserver(() => {
      // Debounce in case of rapid mutations.
      clearTimeout(watchForGridReplace._t);
      watchForGridReplace._t = setTimeout(init, 50);
    }).observe(container, { childList: true, subtree: false });
  }

  // Inject a tiny style for hiding pagination when JS is in control.
  const style = document.createElement('style');
  style.textContent = '.' + PAGE_HIDDEN_CLASS + '{display:none}';
  document.head.appendChild(style);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); watchForGridReplace(); });
  } else {
    init();
    watchForGridReplace();
  }

  // History navigation (browser back/forward through filter states) —
  // facets.js handles the grid swap; the MutationObserver will pick it up.
})();
